/**
 * App-side wiring for `@heidi/session/data` (OpenSpec
 * `monorepo-session-data-core`, first data family D2).
 *
 * Packages are variant-agnostic and store-free (G1/F5): this module injects
 * the app's React Query client singleton so the package's exported COMMANDS
 * (transcript prefetch/invalidate today; the consolidated `invalidateSession*`
 * commands as later families land) address the same cache the app's hooks
 * write. Transport is NOT injected here — `data/api` reads the
 * `@heidi/api/client` registry, which `scribeHTTP.ts` populates at factory
 * time (ARCFE-805); the side-effect import below guarantees that ordering.
 * Imported at module scope in `_app.tsx` next to `workspace-data-wiring`.
 */
// Side-effect: builds the scribe clients AND sets the @heidi/api registry.
import '@/utils/scribeHTTP';

import { isSessionFetchTimeoutEnabled } from '@/features/flags';
import { useQueryLiveTranscriptBySessionId } from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { useAuthToken } from '@/hooks/useIsAuthenticated';
import { useSessionAccessContext } from '@/hooks/useSessionAccessContext';
import { useSessionListEnvironment } from '@/hooks/useSessionListEnvironment';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import { mapNewBrain } from '@/utils/brainUtils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { setUserProperty } from '@/utils/tracing';
import { track } from '@heidi/runtime/analytics';
import { configureSessionData } from '@heidi/session/data';
import { useIsSharedPatientDirectoryEnabled } from '@heidi/workspace/flags';

/**
 * The Datadog-only routing binding for COLAB-1's `trackLinkedSessionsSignal`
 * seam. `amplitude: false` is load-bearing (`enrich.ts` defaults Amplitude ON);
 * `snowplow: false` opts out of the analytics-validator new-event policy. A
 * unit test (`session-data-wiring.test.ts`) pins both options so a future
 * regression to the default routing dies there rather than in production
 * telemetry. Exported for that reason, not for direct use.
 */
export const emitLinkedSessionsSignal: NonNullable<
  Parameters<typeof configureSessionData>[0]['trackLinkedSessionsSignal']
> = (event, props) =>
  track({
    event_name: event,
    props,
    options: { amplitude: false, snowplow: false },
  });

// The D6-era `invalidateSessionsList` seam member healed at D1: the list
// invalidator (`invalidateSessionsQuery`) is now the package's own command,
// reading the SAME client injected here. The three D3 members are static
// imports deliberately: the flag thunk + not-found classifier must be SYNC at
// fetch/retry time (no lazy `import()`), and all three modules are already in
// the `_app` bootstrap graph (flags barrel, capture errors, Datadog init).
configureSessionData({
  getQueryClient: () => reactQueryClient,
  // Thunk, not boolean — the hub reads the flag at FETCH time (see config.ts).
  isSessionFetchTimeoutEnabled: () => isSessionFetchTimeoutEnabled(),
  isSessionNotFoundError,
  onSessionFetched: (sessionId) =>
    setUserProperty({ id: 'session_id', value: sessionId }),
  // D5 (ARCFE-990): the reactive token (S2 — the workspace/commerce shape)
  // and the document brain transform (the app's free-user gating, injected
  // because it composes pricing/UI concerns a data package may not read).
  useAuthToken,
  mapDocumentBrain: (brain, isFreeUser) => mapNewBrain(brain, isFreeUser),
  // The composed `useQuerySessionById` branches on app reads a headless data
  // package may not perform — the router pathname, the `selectedReviewMemberAtom`
  // cohort read, two foreign `/data` doors, the CASL ability and the
  // `useIsAuthenticated()` verdict. They resolve to the eight primitives of
  // `SessionAccessContext` and cross as ONE required hook member. Required, not
  // optional like `useAuthToken` above: an absent wiring would silently change
  // WHICH session a reviewing user sees, and that has no safe degraded form.
  useSessionAccessContext,
  // The combined list read unions offline-recording rows into the list and
  // gates the legacy `/sessions` request on the new-list cohort — two flag
  // reads, a Tauri-backed reachability read and two app atoms, none of which a
  // headless data package may perform. Required for the same reason as the
  // member above it: absent flags would default the legacy request back ON
  // underneath the new list, and React Query dedupes by key, so that one
  // subscriber re-fires it for everyone.
  useSessionListEnvironment,
  // `useIsTranscriptDeleted` must re-render when a re-record refills the live
  // transcript cache, and the hook that subscribes to it is the capture
  // boundary (Redux timer, capture store, live-transcripts flag, tab sharing) —
  // app-side by construction. Same argument, same position; the return value is
  // deliberately dropped, because the package reads the value from the cache.
  useLiveTranscriptSubscription: (sessionId) => {
    useQueryLiveTranscriptBySessionId(sessionId);
  },
  // COLAB-1 seam wiring: the flag hook and the operational-signal sink for
  // the v2.2 linked-sessions cutover. Signal routing is Datadog-only —
  // `amplitude: false` is load-bearing (enrich.ts defaults Amplitude ON) and
  // `snowplow: false` opts out of the analytics-validator new-event policy.
  useIsSharedPatientDirectoryEnabled,
  trackLinkedSessionsSignal: emitLinkedSessionsSignal,
});
